import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Slider,
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  BsZoomIn,
  BsZoomOut,
  BsArrowsFullscreen,
  BsFullscreenExit,
  BsArrowClockwise,
  BsX,
  BsBoundingBoxCircles,
  BsCheckCircleFill,
  BsAspectRatio,
  BsCpu,
  BsStars,
  BsLayoutSplit
} from 'react-icons/bs';
import axios from 'axios';
import OpenSeadragon from 'openseadragon';

const getCandidateApiBases = () => {
  const configured = (process.env.REACT_APP_URL || '/api/').replace(/\/+$/, '') + '/';
  const list = [configured];
  if (!configured.includes('localhost:8000') && !configured.includes('127.0.0.1:8000')) {
    list.push('http://localhost:8000/api/');
    list.push('http://127.0.0.1:8000/api/');
    list.push('/api/');
  }
  return [...new Set(list)];
};

/**
 * Ultra-Performance Deep Zoom & Synchronized AI Super-Pixel Modal
 *
 * Modes:
 *   1. Single View (Full screen Deep Zoom with AI ON/OFF switch)
 *   2. Synchronized Split View (50:50 side-by-side comparison):
 *      - Left: Original Image (pyvips Lanczos3, ai=0)
 *      - Right: AI Super-Pixel Image (Real-ESRGAN Enhanced, ai=1)
 *      - Bidirectional 1:1 synchronized zoom, pan, and rotation locking!
 */
const DeepZoomModal = ({
  open,
  onClose,
  imageUrl,
  title = "Deep Zoom View"
}) => {
  const [loading, setLoading] = useState(true);
  const [imageMeta, setImageMeta] = useState(null);
  const [zoomPercent, setZoomPercent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [aiEnhanced, setAiEnhanced] = useState(true);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const [isSplitView, setIsSplitView] = useState(false);

  const isMountedRef = useRef(true);
  const currentImageUrlRef = useRef(imageUrl);
  currentImageUrlRef.current = imageUrl;
  const aiEnhancedRef = useRef(true);
  aiEnhancedRef.current = aiEnhanced;
  const isSplitViewRef = useRef(false);
  isSplitViewRef.current = isSplitView;

  const activeRequestIdRef = useRef(0);
  const workingApiBaseRef = useRef(getCandidateApiBases()[0]);
  const activeLeadRef = useRef('none');

  // DOM container refs
  const singleContainerRef = useRef(null);
  const leftContainerRef = useRef(null);
  const rightContainerRef = useRef(null);

  // OpenSeadragon instances
  const osdViewerRef = useRef(null);
  const leftViewerRef = useRef(null);
  const rightViewerRef = useRef(null);

  useEffect(() => {
    if (!isEnhancing && imageMeta) {
      setJustFinished(true);
      const timer = setTimeout(() => {
        if (isMountedRef.current) setJustFinished(false);
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isEnhancing, imageMeta]);

  // Clean up all OpenSeadragon instances
  const destroyAllViewers = useCallback(() => {
    if (osdViewerRef.current) {
      try {
        osdViewerRef.current.destroy();
      } catch (err) {
        console.warn("Error destroying single viewer:", err);
      }
      osdViewerRef.current = null;
    }
    if (leftViewerRef.current) {
      try {
        leftViewerRef.current.destroy();
      } catch (err) {
        console.warn("Error destroying left viewer:", err);
      }
      leftViewerRef.current = null;
    }
    if (rightViewerRef.current) {
      try {
        rightViewerRef.current.destroy();
      } catch (err) {
        console.warn("Error destroying right viewer:", err);
      }
      rightViewerRef.current = null;
    }
  }, []);

  const getActiveViewer = useCallback(() => {
    if (isSplitViewRef.current) {
      return leftViewerRef.current || rightViewerRef.current;
    }
    return osdViewerRef.current;
  }, []);

  // Update zoom percentage state based on OpenSeadragon viewport
  const updateZoomState = useCallback(() => {
    const viewer = getActiveViewer();
    if (!viewer || !viewer.viewport) return;
    try {
      const homeZoom = viewer.viewport.getHomeZoom();
      const maxZoom = viewer.viewport.getMaxZoom();
      const currentZoom = viewer.viewport.getZoom(true);
      if (maxZoom > homeZoom) {
        const pct = Math.round(((currentZoom - homeZoom) / (maxZoom - homeZoom)) * 100);
        setZoomPercent(Math.min(100, Math.max(0, pct)));
      } else {
        setZoomPercent(0);
      }
    } catch (e) {
      // ignore
    }
  }, [getActiveViewer]);

  // Construct tileSource specification (Deep Zoom pyramid or single image fallback)
  const buildTileSource = useCallback((meta, imgUrl, isAi, customBase) => {
    if (!meta || meta.directFallback) {
      return {
        type: 'image',
        url: imgUrl
      };
    }
    const base = customBase || workingApiBaseRef.current || (process.env.REACT_APP_URL || '/api/').replace(/\/+$/, '') + '/';
    return {
      width: meta.width,
      height: meta.height,
      tileSize: meta.tile_size || 512,
      minLevel: 0,
      maxLevel: meta.max_level,
      getTileUrl: function(level, x, y) {
        return `${base}deepzoom/tile/?path=${encodeURIComponent(imgUrl)}&level=${level}&x=${x}&y=${y}&size=512&ai=${isAi ? 1 : 0}`;
      }
    };
  }, []);

  const getOsdBaseOptions = useCallback((container, tileSource, showNav) => ({
    element: container,
    prefixUrl: '',
    tileSources: tileSource,
    showNavigationControl: false,
    showNavigator: showNav,
    navigatorPosition: 'BOTTOM_RIGHT',
    navigatorSizeRatio: 0.16,
    navigatorMaintainSizeRatio: true,
    navigatorDisplayOnLogicalBounds: true,
    maxZoomPixelRatio: 3.0,
    minZoomImageRatio: 0.8,
    visibilityRatio: 0.9,
    animationTime: 0.16,
    springStiffness: 15.0,
    imageLoaderLimit: 4,
    blendTime: 0.06,
    alwaysBlend: false,
    preload: true,
    placeholderFillStyle: '#05070c',
    subPixelRoundingForTransparency: true,
    gestureSettingsMouse: {
      clickToZoom: false,
      dblClickToZoom: true,
      pinchToZoom: true,
      scrollToZoom: true
    }
  }), []);

  // Setup Single Viewer mode
  const setupSingleViewer = useCallback((meta, imgUrl, isAi, initialZoom = null, initialCenter = null) => {
    if (!singleContainerRef.current) return;
    destroyAllViewers();

    const tileSource = buildTileSource(meta, imgUrl, isAi, workingApiBaseRef.current);
    const viewer = OpenSeadragon(getOsdBaseOptions(singleContainerRef.current, tileSource, true));

    viewer.addHandler('open', () => {
      if (!isMountedRef.current) return;
      setLoading(false);
      if (initialZoom && initialCenter && viewer.viewport) {
        viewer.viewport.zoomTo(initialZoom, null, true);
        viewer.viewport.panTo(initialCenter, true);
      } else if (viewer.viewport) {
        viewer.viewport.resize();
        viewer.viewport.goHome(true);
      }
      updateZoomState();
    });

    viewer.addHandler('open-failed', () => {
      if (!isMountedRef.current) return;
      setLoading(false);
      const currentUrl = currentImageUrlRef.current;
      if (currentUrl) {
        viewer.open({ type: 'image', url: currentUrl });
      }
    });

    viewer.addHandler('animation', updateZoomState);
    viewer.addHandler('zoom', updateZoomState);
    viewer.addHandler('rotate', (e) => {
      if (!isMountedRef.current) return;
      setRotation(e.degrees);
    });

    let activeTileCount = 0;
    let finishTimer = null;

    viewer.addHandler('tile-loading', () => {
      if (!isMountedRef.current) return;
      activeTileCount++;
      if (finishTimer) {
        clearTimeout(finishTimer);
        finishTimer = null;
      }
      setIsEnhancing(true);
    });

    const onTileDone = () => {
      if (!isMountedRef.current) return;
      activeTileCount = Math.max(0, activeTileCount - 1);
      if (activeTileCount === 0) {
        if (finishTimer) clearTimeout(finishTimer);
        finishTimer = setTimeout(() => {
          if (activeTileCount === 0 && isMountedRef.current) {
            setIsEnhancing(false);
          }
        }, 180);
      }
    };

    viewer.addHandler('tile-loaded', onTileDone);
    viewer.addHandler('tile-load-failed', onTileDone);

    osdViewerRef.current = viewer;
  }, [buildTileSource, destroyAllViewers, getOsdBaseOptions, updateZoomState]);

  // Setup Synchronized Split Viewers (Left: Original ai=0, Right: Super-Pixel ai=1)
  const setupSplitViewers = useCallback((meta, imgUrl, initialZoom = null, initialCenter = null) => {
    if (!leftContainerRef.current || !rightContainerRef.current) return;
    destroyAllViewers();

    const tsLeft = buildTileSource(meta, imgUrl, false, workingApiBaseRef.current);
    const tsRight = buildTileSource(meta, imgUrl, true, workingApiBaseRef.current);

    const viewerLeft = OpenSeadragon(getOsdBaseOptions(leftContainerRef.current, tsLeft, false));
    const viewerRight = OpenSeadragon(getOsdBaseOptions(rightContainerRef.current, tsRight, true));

    leftViewerRef.current = viewerLeft;
    rightViewerRef.current = viewerRight;

    let openedCount = 0;
    const handleBothOpened = () => {
      openedCount++;
      if (openedCount >= 2 && isMountedRef.current) {
        setLoading(false);
        if (initialZoom && initialCenter) {
          viewerLeft.viewport?.zoomTo(initialZoom, null, true);
          viewerLeft.viewport?.panTo(initialCenter, true);
          viewerRight.viewport?.zoomTo(initialZoom, null, true);
          viewerRight.viewport?.panTo(initialCenter, true);
        } else {
          viewerLeft.viewport?.goHome(true);
          viewerRight.viewport?.goHome(true);
        }
        updateZoomState();
      }
    };

    viewerLeft.addHandler('open', handleBothOpened);
    viewerRight.addHandler('open', handleBothOpened);

    // Bidirectional 1:1 Synchronized Viewport Locking
    viewerLeft.addHandler('animation', () => {
      if (activeLeadRef.current === 'left' && viewerRight?.viewport) {
        viewerRight.viewport.zoomTo(viewerLeft.viewport.getZoom(true), null, true);
        viewerRight.viewport.panTo(viewerLeft.viewport.getCenter(true), true);
        viewerRight.viewport.applyConstraints();
      }
      updateZoomState();
    });

    viewerRight.addHandler('animation', () => {
      if (activeLeadRef.current === 'right' && viewerLeft?.viewport) {
        viewerLeft.viewport.zoomTo(viewerRight.viewport.getZoom(true), null, true);
        viewerLeft.viewport.panTo(viewerRight.viewport.getCenter(true), true);
        viewerLeft.viewport.applyConstraints();
      }
      updateZoomState();
    });

    // Handle rotation sync
    viewerLeft.addHandler('rotate', (e) => {
      if (!isMountedRef.current) return;
      setRotation(e.degrees);
      if (viewerRight?.viewport && viewerRight.viewport.getRotation() !== e.degrees) {
        viewerRight.viewport.setRotation(e.degrees);
      }
    });

    viewerRight.addHandler('rotate', (e) => {
      if (!isMountedRef.current) return;
      setRotation(e.degrees);
      if (viewerLeft?.viewport && viewerLeft.viewport.getRotation() !== e.degrees) {
        viewerLeft.viewport.setRotation(e.degrees);
      }
    });

    // Track AI tile loading status from right viewer
    let activeTileCount = 0;
    let finishTimer = null;

    viewerRight.addHandler('tile-loading', () => {
      if (!isMountedRef.current) return;
      activeTileCount++;
      if (finishTimer) {
        clearTimeout(finishTimer);
        finishTimer = null;
      }
      setIsEnhancing(true);
    });

    const onTileDone = () => {
      if (!isMountedRef.current) return;
      activeTileCount = Math.max(0, activeTileCount - 1);
      if (activeTileCount === 0) {
        if (finishTimer) clearTimeout(finishTimer);
        finishTimer = setTimeout(() => {
          if (activeTileCount === 0 && isMountedRef.current) {
            setIsEnhancing(false);
          }
        }, 180);
      }
    };

    viewerRight.addHandler('tile-loaded', onTileDone);
    viewerRight.addHandler('tile-load-failed', onTileDone);
  }, [buildTileSource, destroyAllViewers, getOsdBaseOptions, updateZoomState]);

  // Initial loading effect
  useEffect(() => {
    isMountedRef.current = true;
    if (!open) {
      destroyAllViewers();
      return;
    }

    if (!imageUrl) {
      setLoading(true);
      return;
    }

    const requestId = ++activeRequestIdRef.current;
    setLoading(true);
    setZoomPercent(0);
    setRotation(0);

    const candidates = getCandidateApiBases();
    let candidateIndex = 0;

    const tryNextCandidate = () => {
      if (candidateIndex >= candidates.length) {
        fallbackInit();
        return;
      }
      const testBase = candidates[candidateIndex++];
      const infoUrl = `${testBase}deepzoom/info/?path=${encodeURIComponent(imageUrl)}`;

      axios.get(infoUrl, { timeout: 4000 })
        .then((res) => {
          if (!isMountedRef.current || requestId !== activeRequestIdRef.current) return;
          const meta = res.data;
          if (meta && meta.success && meta.width && meta.height) {
            workingApiBaseRef.current = testBase;
            setImageMeta(meta);

            if (isSplitViewRef.current) {
              setupSplitViewers(meta, imageUrl);
            } else {
              setupSingleViewer(meta, imageUrl, aiEnhancedRef.current);
            }
          } else {
            tryNextCandidate();
          }
        })
        .catch(() => {
          if (!isMountedRef.current || requestId !== activeRequestIdRef.current) return;
          tryNextCandidate();
        });
    };

    tryNextCandidate();

    function fallbackInit() {
      const testImg = new Image();
      testImg.crossOrigin = "anonymous";
      testImg.src = imageUrl;
      testImg.onload = () => {
        if (!isMountedRef.current || requestId !== activeRequestIdRef.current) return;
        const w = testImg.naturalWidth || 1920;
        const h = testImg.naturalHeight || 1080;
        const meta = {
          success: true,
          width: w,
          height: h,
          directFallback: true
        };
        setImageMeta(meta);
        if (isSplitViewRef.current) {
          setupSplitViewers(meta, imageUrl);
        } else {
          setupSingleViewer(meta, imageUrl, false);
        }
      };
      testImg.onerror = () => {
        if (!isMountedRef.current || requestId !== activeRequestIdRef.current) return;
        setLoading(false);
        if (isSplitViewRef.current) {
          setupSplitViewers({ directFallback: true }, imageUrl);
        } else {
          setupSingleViewer({ directFallback: true }, imageUrl, false);
        }
      };
    }
  }, [open, imageUrl, setupSingleViewer, setupSplitViewers, destroyAllViewers]);

  // Clean up viewers on unmount or close
  useEffect(() => {
    if (!open) {
      destroyAllViewers();
    }
    return () => {
      isMountedRef.current = false;
      destroyAllViewers();
    };
  }, [open, destroyAllViewers]);

  // Toggle between Single View and Synchronized Split View
  const toggleSplitView = () => {
    if (!imageMeta || !currentImageUrlRef.current) return;
    const currentViewer = getActiveViewer();
    const currentZoom = currentViewer?.viewport?.getZoom() || null;
    const currentCenter = currentViewer?.viewport?.getCenter() || null;

    const nextSplit = !isSplitView;
    setIsSplitView(nextSplit);
    isSplitViewRef.current = nextSplit;

    setTimeout(() => {
      if (!isMountedRef.current) return;
      if (nextSplit) {
        setupSplitViewers(imageMeta, currentImageUrlRef.current, currentZoom, currentCenter);
      } else {
        setupSingleViewer(imageMeta, currentImageUrlRef.current, aiEnhancedRef.current, currentZoom, currentCenter);
      }
    }, 40);
  };

  // Zoom controls (works synchronously for both Single and Split views)
  const handleZoomIn = () => {
    activeLeadRef.current = 'none';
    if (isSplitViewRef.current) {
      leftViewerRef.current?.viewport?.zoomBy(1.35);
      rightViewerRef.current?.viewport?.zoomBy(1.35);
      leftViewerRef.current?.viewport?.applyConstraints();
      rightViewerRef.current?.viewport?.applyConstraints();
    } else {
      osdViewerRef.current?.viewport?.zoomBy(1.35);
      osdViewerRef.current?.viewport?.applyConstraints();
    }
  };

  const handleZoomOut = () => {
    activeLeadRef.current = 'none';
    if (isSplitViewRef.current) {
      leftViewerRef.current?.viewport?.zoomBy(0.74);
      rightViewerRef.current?.viewport?.zoomBy(0.74);
      leftViewerRef.current?.viewport?.applyConstraints();
      rightViewerRef.current?.viewport?.applyConstraints();
    } else {
      osdViewerRef.current?.viewport?.zoomBy(0.74);
      osdViewerRef.current?.viewport?.applyConstraints();
    }
  };

  const handleReset = () => {
    activeLeadRef.current = 'none';
    if (isSplitViewRef.current) {
      leftViewerRef.current?.viewport?.goHome(true);
      rightViewerRef.current?.viewport?.goHome(true);
    } else {
      osdViewerRef.current?.viewport?.goHome(true);
    }
    setZoomPercent(0);
  };

  const handleNative1To1 = () => {
    if (!imageMeta) return;
    activeLeadRef.current = 'none';
    const extra = Math.max(0, (imageMeta.max_level || 11) - (imageMeta.base_max_level || 11));
    const nativeRatio = 1.0 / (2 ** extra);

    if (isSplitViewRef.current) {
      const vL = leftViewerRef.current;
      const vR = rightViewerRef.current;
      if (vL?.viewport) {
        const z = vL.viewport.imageToViewportZoom(nativeRatio);
        vL.viewport.zoomTo(z, null, false);
        vL.viewport.applyConstraints();
      }
      if (vR?.viewport) {
        const z = vR.viewport.imageToViewportZoom(nativeRatio);
        vR.viewport.zoomTo(z, null, false);
        vR.viewport.applyConstraints();
      }
    } else if (osdViewerRef.current?.viewport) {
      const v = osdViewerRef.current;
      const z = v.viewport.imageToViewportZoom(nativeRatio);
      v.viewport.zoomTo(z, null, false);
      v.viewport.applyConstraints();
    }
  };

  const handleRotate = (deg) => {
    activeLeadRef.current = 'none';
    if (isSplitViewRef.current) {
      if (leftViewerRef.current?.viewport) {
        const rot = leftViewerRef.current.viewport.getRotation();
        leftViewerRef.current.viewport.setRotation(rot + deg);
      }
      if (rightViewerRef.current?.viewport) {
        const rot = rightViewerRef.current.viewport.getRotation();
        rightViewerRef.current.viewport.setRotation(rot + deg);
      }
    } else if (osdViewerRef.current?.viewport) {
      const rot = osdViewerRef.current.viewport.getRotation();
      osdViewerRef.current.viewport.setRotation(rot + deg);
    }
  };

  const handleSliderChange = (_, val) => {
    activeLeadRef.current = 'none';
    if (isSplitViewRef.current) {
      const vL = leftViewerRef.current;
      const vR = rightViewerRef.current;
      if (vL?.viewport) {
        const home = vL.viewport.getHomeZoom();
        const max = vL.viewport.getMaxZoom();
        const target = home + (val / 100.0) * (max - home);
        vL.viewport.zoomTo(target, null, true);
      }
      if (vR?.viewport) {
        const home = vR.viewport.getHomeZoom();
        const max = vR.viewport.getMaxZoom();
        const target = home + (val / 100.0) * (max - home);
        vR.viewport.zoomTo(target, null, true);
      }
    } else if (osdViewerRef.current?.viewport) {
      const v = osdViewerRef.current;
      const home = v.viewport.getHomeZoom();
      const max = v.viewport.getMaxZoom();
      const target = home + (val / 100.0) * (max - home);
      v.viewport.zoomTo(target, null, true);
    }
  };

  const toggleAiEnhance = () => {
    const nextAi = !aiEnhanced;
    setAiEnhanced(nextAi);
    aiEnhancedRef.current = nextAi;

    const viewer = osdViewerRef.current;
    if (!viewer || !viewer.viewport || !imageMeta || !currentImageUrlRef.current) return;

    const currentCenter = viewer.viewport.getCenter();
    const currentZoom = viewer.viewport.getZoom();

    const newTileSource = buildTileSource(imageMeta, currentImageUrlRef.current, nextAi, workingApiBaseRef.current);

    viewer.addTiledImage({
      tileSource: newTileSource,
      success: () => {
        setTimeout(() => {
          try {
            if (viewer.world && viewer.world.getItemCount() > 1) {
              viewer.world.removeItem(viewer.world.getItemAt(0));
            }
            if (viewer.viewport) {
              viewer.viewport.zoomTo(currentZoom, null, false);
              viewer.viewport.panTo(currentCenter, false);
            }
          } catch (e) {}
        }, 120);
      },
      error: (err) => {
        console.warn("Could not transition tiled image:", err);
      }
    });
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  // Keyboard navigation shortcuts
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-' || e.key === '_') {
        handleZoomOut();
      } else if (e.key === '0' || e.key === 'Home') {
        handleReset();
      } else if (e.key === '1') {
        handleNative1To1();
      } else if (e.key === 's' || e.key === 'S') {
        toggleSplitView();
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, toggleSplitView, handleZoomIn, handleZoomOut, handleReset, handleNative1To1, toggleFullscreen]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 99999,
        '& .MuiDialog-paper': {
          backgroundColor: '#05070c',
          color: '#fff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Top Header Bar */}
      <Box
        sx={{
          height: 54,
          px: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: 'rgba(15, 23, 42, 0.94)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
          zIndex: 100,
          userSelect: 'none',
          backdropFilter: 'blur(12px)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BsBoundingBoxCircles size={18} color="#38bdf8" />
          <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: 0.5, color: '#f8fafc' }}>
            {title}
          </Typography>

          {imageMeta?.native_width ? (
            <Chip
              size="small"
              label={`${imageMeta.native_width} × ${imageMeta.native_height} px`}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: '#94a3b8',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          ) : imageMeta?.width ? (
            <Chip
              size="small"
              label={`${imageMeta.width} × ${imageMeta.height} px`}
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: '#94a3b8',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)'
              }}
            />
          ) : null}

          {isSplitView ? (
            <Chip
              size="small"
              icon={<BsLayoutSplit size={12} color="#c084fc" style={{ marginLeft: 6 }} />}
              label="Synchronized Split View (50:50)"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: '#c084fc',
                backgroundColor: 'rgba(168, 85, 247, 0.18)',
                border: '1px solid rgba(168, 85, 247, 0.45)',
                boxShadow: '0 0 12px rgba(168, 85, 247, 0.3)'
              }}
            />
          ) : aiEnhanced ? (
            <Chip
              size="small"
              icon={<BsStars size={12} color="#4ade80" style={{ marginLeft: 6 }} />}
              label="Real-ESRGAN (AI Super-Pixel HD)"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: '#4ade80',
                backgroundColor: 'rgba(34, 197, 94, 0.16)',
                border: '1px solid rgba(34, 197, 94, 0.45)',
                boxShadow: '0 0 12px rgba(34, 197, 94, 0.3)'
              }}
            />
          ) : (
            <Chip
              size="small"
              icon={<BsCpu size={12} color="#38bdf8" style={{ marginLeft: 6 }} />}
              label="pyvips Lanczos3 (Standard)"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: '#38bdf8',
                backgroundColor: 'rgba(56, 189, 248, 0.12)',
                border: '1px solid rgba(56, 189, 248, 0.3)'
              }}
            />
          )}

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#38bdf8', fontSize: 11, fontWeight: 600 }}>
              <CircularProgress size={12} sx={{ color: '#38bdf8' }} />
              <span>Initializing...</span>
            </Box>
          ) : isEnhancing ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, color: '#ffd80c', fontSize: 11, fontWeight: 700 }}>
              <CircularProgress size={12} sx={{ color: '#ffd80c' }} />
              <span>AI Enhancing...</span>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#4ade80', fontSize: 11, fontWeight: 600 }}>
              <BsCheckCircleFill size={11} />
              <span>{isSplitView ? "Split Compare Ready" : aiEnhanced ? "AI Super-Pixel Ready" : "Deep Zoom Ready"}</span>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: '#38bdf8',
              backgroundColor: 'rgba(56, 189, 248, 0.12)',
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              border: '1px solid rgba(56, 189, 248, 0.3)',
              mr: 1
            }}
          >
            {zoomPercent === 0 ? "0% (Fit)" : `${zoomPercent}%`}
          </Box>

          {/* Header Split View Toggle Button */}
          <Tooltip title={isSplitView ? "Switch to Single View (S)" : "Switch to Synchronized Split View (S)"}>
            <Box
              onClick={toggleSplitView}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.7,
                px: 1.4,
                py: 0.5,
                borderRadius: 1.5,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
                userSelect: 'none',
                transition: 'all 0.2s ease',
                backgroundColor: isSplitView ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                color: isSplitView ? '#c084fc' : '#cbd5e1',
                border: isSplitView ? '1px solid rgba(168, 85, 247, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: isSplitView ? '0 0 14px rgba(168, 85, 247, 0.4)' : 'none',
                mr: 0.5,
                '&:hover': {
                  backgroundColor: isSplitView ? 'rgba(168, 85, 247, 0.35)' : 'rgba(255, 255, 255, 0.15)',
                  color: '#fff',
                }
              }}
            >
              <BsLayoutSplit size={14} color={isSplitView ? '#c084fc' : '#cbd5e1'} />
              <span>{isSplitView ? "Split: ON" : "Split View"}</span>
            </Box>
          </Tooltip>

          <Tooltip title={isFullscreen ? "Exit Fullscreen (F)" : "Fullscreen (F)"}>
            <IconButton onClick={toggleFullscreen} sx={{ color: '#e2e8f0', '&:hover': { color: '#fff' } }}>
              {isFullscreen ? <BsFullscreenExit size={18} /> : <BsArrowsFullscreen size={18} />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Close (Esc)">
            <IconButton onClick={onClose} sx={{ color: '#f43f5e', '&:hover': { backgroundColor: 'rgba(244,63,94,0.15)' } }}>
              <BsX size={26} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Dedicated Viewport Wrapper with OpenSeadragon Container(s) */}
      <Box
        sx={{
          width: '100vw',
          height: 'calc(100vh - 54px)',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#05070c',
          '& .openseadragon-container': {
            width: '100% !important',
            height: '100% !important'
          },
          // Custom Sleek Navigator (Minimap) Styling
          '& .navigator': {
            borderRadius: '8px !important',
            border: '1px solid rgba(56, 189, 248, 0.4) !important',
            backgroundColor: 'rgba(15, 23, 42, 0.85) !important',
            boxShadow: '0 8px 32px rgba(0,0,0,0.7) !important',
            overflow: 'hidden !important',
            bottom: '92px !important',
            right: '20px !important'
          },
          '& .navigator .displayregion': {
            border: '2px solid #38bdf8 !important',
            backgroundColor: 'rgba(56, 189, 248, 0.25) !important'
          }
        }}
      >
        {isSplitView ? (
          /* Synchronized Split Screen Comparison */
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Left Panel: Original Image (ai=0) */}
            <Box
              onPointerDown={() => { activeLeadRef.current = 'left'; }}
              onWheel={() => { activeLeadRef.current = 'left'; }}
              onTouchStart={() => { activeLeadRef.current = 'left'; }}
              sx={{
                flex: 1,
                height: '100%',
                position: 'relative',
                borderRight: '2px solid rgba(56, 189, 248, 0.4)',
                overflow: 'hidden'
              }}
            >
              <div
                ref={leftContainerRef}
                id="openseadragon-viewer-left"
                style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
              />
              {/* Floating Badge Left */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  left: 20,
                  zIndex: 90,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  background: 'rgba(15, 23, 42, 0.88)',
                  backdropFilter: 'blur(10px)',
                  border: '1.2px solid rgba(56, 189, 248, 0.5)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                  borderRadius: '20px',
                  px: 1.8,
                  py: 0.6,
                  color: '#38bdf8',
                  fontSize: 12,
                  fontWeight: 700,
                  pointerEvents: 'none'
                }}
              >
                <BsCpu size={14} color="#38bdf8" />
                <span>Original (Standard)</span>
              </Box>
            </Box>

            {/* Center Synchronized Locking Badge */}
            <Box
              sx={{
                position: 'absolute',
                top: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 95,
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                background: 'rgba(15, 23, 42, 0.92)',
                backdropFilter: 'blur(12px)',
                border: '1.5px solid rgba(168, 85, 247, 0.6)',
                boxShadow: '0 4px 20px rgba(168, 85, 247, 0.4)',
                borderRadius: '20px',
                px: 2,
                py: 0.6,
                color: '#c084fc',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 0.5,
                pointerEvents: 'none',
                textTransform: 'uppercase'
              }}
            >
              <BsLayoutSplit size={14} />
              <span>1:1 Synced Zoom & Pan</span>
            </Box>

            {/* Right Panel: AI Super-Pixel (ai=1) */}
            <Box
              onPointerDown={() => { activeLeadRef.current = 'right'; }}
              onWheel={() => { activeLeadRef.current = 'right'; }}
              onTouchStart={() => { activeLeadRef.current = 'right'; }}
              sx={{
                flex: 1,
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <div
                ref={rightContainerRef}
                id="openseadragon-viewer-right"
                style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
              />
              {/* Floating Badge Right */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  right: 20,
                  zIndex: 90,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  background: 'rgba(15, 23, 42, 0.88)',
                  backdropFilter: 'blur(10px)',
                  border: '1.2px solid rgba(74, 222, 128, 0.5)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                  borderRadius: '20px',
                  px: 1.8,
                  py: 0.6,
                  color: '#4ade80',
                  fontSize: 12,
                  fontWeight: 700,
                  pointerEvents: 'none'
                }}
              >
                <BsStars size={14} color="#4ade80" />
                <span>AI Super-Pixel (Enhanced)</span>
              </Box>
            </Box>
          </Box>
        ) : (
          /* Single Viewer */
          <div
            ref={singleContainerRef}
            id="openseadragon-viewer"
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
          />
        )}

        {/* Floating AI Super-Pixel Status Badge during zoom / pan */}
        {isEnhancing && (
          <Box
            sx={{
              position: 'absolute',
              top: 75,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 90,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              background: 'rgba(5, 7, 12, 0.90)',
              backdropFilter: 'blur(12px)',
              border: '1.5px solid #38bdf8',
              boxShadow: '0 4px 24px rgba(56, 189, 248, 0.45)',
              borderRadius: '24px',
              px: 2.5,
              py: 0.9,
              color: '#fff',
              pointerEvents: 'none'
            }}
          >
            <CircularProgress size={16} sx={{ color: '#38bdf8' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3, color: '#f0f9ff' }}>
              {isSplitView ? "AI Super-Pixel Enhancing Right Side..." : aiEnhanced ? "AI Super-Pixel Enhancing... (Please wait)" : "Loading Deep Zoom Tiles..."}
            </Typography>
          </Box>
        )}

        {!isEnhancing && justFinished && (
          <Box
            sx={{
              position: 'absolute',
              top: 75,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 90,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              background: 'rgba(5, 7, 12, 0.88)',
              backdropFilter: 'blur(10px)',
              border: '1.5px solid #22c55e',
              boxShadow: '0 4px 20px rgba(34, 197, 94, 0.35)',
              borderRadius: '24px',
              px: 2.2,
              py: 0.8,
              color: '#4ade80',
              pointerEvents: 'none'
            }}
          >
            <BsCheckCircleFill size={15} color="#4ade80" />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>
              {isSplitView ? "Synchronized Comparison Ready" : aiEnhanced ? "AI Super-Pixel Enhanced (High Clarity Ready)" : "Deep Zoom Ready"}
            </Typography>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              backgroundColor: 'rgba(5, 7, 12, 0.75)',
              zIndex: 50,
              pointerEvents: 'none'
            }}
          >
            <CircularProgress size={44} sx={{ color: '#38bdf8' }} />
            <Typography variant="body2" color="#94a3b8">
              Initializing OpenSeadragon & pyvips tile engine...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom Floating Control Dock */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2.5,
          py: 1,
          backgroundColor: 'rgba(15, 23, 42, 0.92)',
          borderRadius: 8,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(12px)',
          userSelect: 'none'
        }}
      >
        <Tooltip title="Zoom Out (-)">
          <IconButton
            size="small"
            onClick={handleZoomOut}
            sx={{ color: '#f8fafc', '&:hover': { color: '#38bdf8' } }}
          >
            <BsZoomOut size={18} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: 140 }}>
          <Slider
            value={zoomPercent}
            min={0}
            max={100}
            step={1}
            onChange={handleSliderChange}
            sx={{
              color: isSplitView ? '#c084fc' : '#38bdf8',
              height: 4,
              '& .MuiSlider-thumb': {
                width: 14,
                height: 14,
                backgroundColor: '#fff',
                '&:hover, &.Mui-focusVisible': {
                  boxShadow: isSplitView ? '0 0 0 8px rgba(168, 85, 247, 0.25)' : '0 0 0 8px rgba(56, 189, 248, 0.2)'
                }
              },
              '& .MuiSlider-rail': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
          />
        </Box>

        <Tooltip title="Zoom In (+)">
          <IconButton
            size="small"
            onClick={handleZoomIn}
            sx={{ color: '#f8fafc', '&:hover': { color: '#38bdf8' } }}
          >
            <BsZoomIn size={18} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: '1px', height: 20, backgroundColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

        {/* 1:1 Native Resolution Jump */}
        <Tooltip title="1:1 Native Sensor Resolution (100% pixel-to-pixel)">
          <IconButton
            size="small"
            onClick={handleNative1To1}
            sx={{
              color: zoomPercent >= 95 && zoomPercent <= 105 ? (isSplitView ? '#c084fc' : '#38bdf8') : '#f8fafc',
              backgroundColor: zoomPercent >= 95 && zoomPercent <= 105 ? (isSplitView ? 'rgba(168, 85, 247, 0.25)' : 'rgba(56, 189, 248, 0.2)') : 'transparent',
              fontSize: 11,
              fontWeight: 700,
              px: 1,
              borderRadius: 1.5,
              '&:hover': { color: isSplitView ? '#c084fc' : '#38bdf8', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            1:1
          </IconButton>
        </Tooltip>

        {/* Fit to Screen / Reset */}
        <Tooltip title="Fit to Screen (Home / 0)">
          <IconButton
            size="small"
            onClick={handleReset}
            sx={{ color: '#f8fafc', '&:hover': { color: isSplitView ? '#c084fc' : '#38bdf8' } }}
          >
            <BsAspectRatio size={16} />
          </IconButton>
        </Tooltip>

        {/* Rotate Clockwise */}
        <Tooltip title="Rotate 90°">
          <IconButton
            size="small"
            onClick={() => handleRotate(90)}
            sx={{ color: '#f8fafc', '&:hover': { color: isSplitView ? '#c084fc' : '#38bdf8' } }}
          >
            <BsArrowClockwise size={16} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: '1px', height: 20, backgroundColor: 'rgba(255,255,255,0.2)', mx: 0.5 }} />

        {/* Synchronized Split View Comparison Toggle Button */}
        <Tooltip title={isSplitView ? "Switch back to Single View (S)" : "Side-by-Side Comparison: Original vs AI Super-Pixel (S)"}>
          <Box
            onClick={toggleSplitView}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.6,
              py: 0.6,
              borderRadius: 2,
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 700,
              userSelect: 'none',
              transition: 'all 0.25s ease',
              backgroundColor: isSplitView ? 'rgba(168, 85, 247, 0.25)' : 'rgba(255, 255, 255, 0.08)',
              color: isSplitView ? '#c084fc' : '#cbd5e1',
              border: isSplitView ? '1px solid rgba(168, 85, 247, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: isSplitView ? '0 0 16px rgba(168, 85, 247, 0.45)' : 'none',
              '&:hover': {
                backgroundColor: isSplitView ? 'rgba(168, 85, 247, 0.35)' : 'rgba(255, 255, 255, 0.15)',
                color: '#fff',
                transform: 'scale(1.03)'
              }
            }}
          >
            <BsLayoutSplit size={15} color={isSplitView ? '#c084fc' : '#94a3b8'} />
            <span>{isSplitView ? "Split: ON" : "Split Compare"}</span>
          </Box>
        </Tooltip>

        {/* AI HD Enhancement Toggle Button (Available in Single View) */}
        {!isSplitView && (
          <Tooltip title={aiEnhanced ? "Disable AI Enhancement (Switch to Fast Lanczos3)" : "Enable AI HD Enhancement (Real-ESRGAN Edge Reconstruction)"}>
            <Box
              onClick={toggleAiEnhance}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                px: 1.5,
                py: 0.6,
                borderRadius: 2,
                cursor: 'pointer',
                fontSize: 11,
                fontWeight: 700,
                userSelect: 'none',
                transition: 'all 0.25s ease',
                backgroundColor: aiEnhanced ? 'rgba(34, 197, 94, 0.22)' : 'rgba(255, 255, 255, 0.08)',
                color: aiEnhanced ? '#4ade80' : '#94a3b8',
                border: aiEnhanced ? '1px solid rgba(34, 197, 94, 0.55)' : '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: aiEnhanced ? '0 0 14px rgba(34, 197, 94, 0.35)' : 'none',
                '&:hover': {
                  backgroundColor: aiEnhanced ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.14)',
                  color: aiEnhanced ? '#86efac' : '#f8fafc',
                  transform: 'scale(1.03)'
                }
              }}
            >
              <BsStars size={15} color={aiEnhanced ? '#4ade80' : '#94a3b8'} />
              <span>{aiEnhanced ? "AI HD: ON" : "AI HD: OFF"}</span>
            </Box>
          </Tooltip>
        )}
      </Box>
    </Dialog>
  );
};

export default DeepZoomModal;
